import { Icon } from '../ui/Icon';
import { Button, Card, Skeleton } from '../ui/primitives';
import type { NotesView } from '../../utils/inicioView';
import { CardHead, DoneBox, Tile } from './InicioParts';

const TITLE_ID = 'inicio-notes-title';

/**
 * Rediseño · Bloque C — «Lo que noté» (P-021, P-030).
 *
 * Son los mensajes del motor de insights que ya existía: como máximo tres, cada uno con su titular,
 * qué significa, por qué importa y a dónde ir. Cuando no hay nada material, se dice — con lo que se
 * revisó, porque el silencio sin evidencia se lee como que la app falla.
 *
 * Lo que el diseño dibuja y aquí no: los insights de descubrimiento (el producto más vendido, la
 * hora más fuerte, el patrón de seis semanas) y su línea de evidencia. El motor todavía no los
 * genera, y escribirlos a mano sería inventarlos. Tampoco hay «Ver todos»: la bandeja de Avisos es
 * una fase funcional futura (B0-12).
 *
 * `view` en `null` es «todavía cargando»: se dibujan los marcadores, no un mensaje provisional.
 */
export function InicioNotes({ view, onNavigate }: { view: NotesView | null; onNavigate: (destination: string) => void }) {
  if (!view) {
    return (
      <Card aria-busy="true" aria-labelledby={TITLE_ID} as="section">
        <CardHead icon={<Icon name="bulb" size={22} />} id={TITLE_ID} title="Lo que noté" />
        <span className="n3-visually-hidden" role="status">Cargando…</span>
        <div className="n3-inicio__notes">
          <Skeleton height="96px" />
          <Skeleton height="96px" />
          <Skeleton height="96px" />
        </div>
      </Card>
    );
  }
  return (
    <Card aria-labelledby={TITLE_ID} as="section">
      <CardHead icon={<Icon name="bulb" size={22} />} id={TITLE_ID} title="Lo que noté" />
      {view.quiet && <DoneBox text={view.quiet.evidence} title={view.quiet.headline} />}
      {view.notes.length > 0 && (
        <ul className="n3-inicio__notes">
          {view.notes.map((note) => (
            <Card as="li" className="n3-inicio__note" key={note.id} padding="sm" radius="lg" tone={note.tone}>
              <Tile icon={note.icon} tone="surface" />
              <div className="n3-inicio__note-body">
                {/* Dato → qué significa → por qué importa → qué revisar. */}
                <p className="n3-inicio__note-headline">{note.headline}</p>
                <p className="n3-inicio__note-text">{note.meaning}</p>
                <p className="n3-inicio__note-text">{note.whyItMatters}</p>
              </div>
              {note.cta && <Button className="n3-inicio__note-action" onClick={() => onNavigate(note.cta!.destination)}>{note.cta.label}</Button>}
            </Card>
          ))}
        </ul>
      )}
      {view.moreLabel && <p className="n3-inicio__support n3-inicio__notes-more">{view.moreLabel}</p>}
    </Card>
  );
}
